import { useEffect, useState } from "react";
import { Plus, Stethoscope, Trash2, Pencil, Check, X } from "lucide-react";
import api, { inr } from "../api";

const EMPTY = { name: "", category: "General", price: "", duration: "30 min", description: "" };

export default function Treatments() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/connect/treatments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canDelete = ["owner", "clinic_manager", "super_admin", "admin"].includes(me.level || "owner");
  const canEdit = canDelete;
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState("");

  const toggleActive = async (t) => {
    await api.patch(`/connect/treatments/${t.id}`, { active: t.active === false });
    load();
  };

  const savePrice = async (id) => {
    await api.patch(`/connect/treatments/${id}`, { price: Number(editPrice) || 0 });
    setEditingId(null);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/connect/treatments/${id}`);
    load();
  };

  const add = async (e) => {
    e.preventDefault();
    await api.post("/connect/treatments", { ...form, price: Number(form.price) || 0 });
    setForm(EMPTY);
    setShowForm(false);
    load();
  };

  return (
    <div data-testid="connect-treatments" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Treatments</h2>
          <p className="text-sm text-slate-500">Your clinic's treatment catalogue with pricing.</p>
        </div>
        <button data-testid="treatments-add-toggle" onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 rounded-full bg-pine px-5 py-2.5 text-xs font-bold text-ivory shadow-lift hover:bg-pine-deep transition-all">
          <Plus className="h-4 w-4" /> Add Treatment
        </button>
      </div>

      {showForm && (
        <form data-testid="treatments-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <input required data-testid="treatment-name" placeholder="Treatment name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <select data-testid="treatment-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine">
            {["General", "Preventive", "Restorative", "Cosmetic", "Orthodontic", "Dermatology"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input required data-testid="treatment-price" type="number" min="0" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="treatment-duration" placeholder="Duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
          <input data-testid="treatment-description" placeholder="Description / notes (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine sm:col-span-2 lg:col-span-4" />
          <button data-testid="treatment-submit" className="h-11 rounded-full bg-teal-brand text-white text-xs font-bold hover:bg-pine transition-colors">Save</button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`treatment-card-${t.id.slice(0, 8)}`} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 hover:-translate-y-0.5 hover:shadow-lift transition-all">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-soft text-teal-brand"><Stethoscope className="h-5 w-5" strokeWidth={1.9} /></span>
            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="font-bold text-pine-deep">{t.name}</p>
              {canEdit && (
                <button data-testid={`treatment-active-${t.id.slice(0, 8)}`} onClick={() => toggleActive(t)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold transition-colors ${t.active === false ? "bg-red-50 text-red-500" : "bg-teal-soft text-teal-brand"}`}
                  aria-label={`Toggle ${t.name} active`}>
                  {t.active === false ? "Inactive" : "Active"}
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">{t.category} · {t.duration}</p>
            {t.description && <p className="mt-1 text-[11px] text-slate-400 line-clamp-2">{t.description}</p>}
            {editingId === t.id ? (
              <div className="mt-3 flex items-center gap-2">
                <input
                  data-testid={`treatment-price-input-${t.id.slice(0, 8)}`}
                  type="number" min="0" value={editPrice} onChange={(e) => setEditPrice(e.target.value)}
                  className="h-10 w-28 rounded-xl border border-pine/15 px-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-pine"
                />
                <button data-testid={`treatment-price-save-${t.id.slice(0, 8)}`} onClick={() => savePrice(t.id)} className="h-8 w-8 rounded-full bg-pine text-ivory flex items-center justify-center hover:bg-pine-deep" aria-label="Save price"><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditingId(null)} className="h-8 w-8 rounded-full border border-pine/15 text-pine flex items-center justify-center hover:bg-cream" aria-label="Cancel"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <p className="mt-3 font-display text-xl font-semibold text-pine-deep inline-flex items-center gap-2">
                {inr(t.price)}
                {canEdit && (
                  <button data-testid={`treatment-price-edit-${t.id.slice(0, 8)}`} onClick={() => { setEditingId(t.id); setEditPrice(String(t.price)); }} className="text-pine/40 hover:text-teal-brand transition-colors" aria-label={`Edit price of ${t.name}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </p>
            )}
            {canDelete && (
              <button data-testid={`treatment-delete-${t.id.slice(0, 8)}`} onClick={() => remove(t.id)} className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:text-red-600" aria-label={`Delete ${t.name}`}>
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
